import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2, X, ExternalLink } from "lucide-react";
import { generateBrochure } from "@/lib/lazyPdfGenerator";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

interface BrochurePreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BrochurePreview = ({ open, onOpenChange }: BrochurePreviewProps) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('brochure.pdf');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let revoked = false;
    let urlToRevoke: string | null = null;

    const build = async () => {
      setLoading(true);
      try {
        const result = await generateBrochure(language, { output: 'blob' });
        if (revoked) return;
        if (result?.blob) {
          const url = URL.createObjectURL(result.blob);
          urlToRevoke = url;
          setPdfUrl(url);
          setFilename(result.filename);
        }
      } catch (e) {
        toast.error(isArabic ? 'فشل إنشاء المعاينة' : 'Failed to generate preview');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    };

    build();

    return () => {
      revoked = true;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
      setPdfUrl(null);
    };
  }, [open, language, isArabic, onOpenChange]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(isArabic ? 'تم تنزيل الكتيب بنجاح' : 'Brochure downloaded successfully');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-xl font-bold">
            {isArabic ? 'معاينة كتيب الشركة' : 'Company Brochure Preview'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isArabic
              ? 'استعرض الكتيب الجديد مع جميع صفحات المنشورات قبل التنزيل.'
              : 'Review the redesigned brochure with all flyer pages before downloading.'}
          </p>
        </DialogHeader>

        <div className="flex-1 bg-muted/30 overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {isArabic ? 'جاري إنشاء المعاينة...' : 'Generating preview...'}
              </p>
            </div>
          )}
          {pdfUrl && !loading && (
            <object
              data={`${pdfUrl}#view=FitH&toolbar=1`}
              type="application/pdf"
              className="w-full h-full"
              aria-label={isArabic ? 'معاينة الكتيب' : 'Brochure preview'}
            >
              <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  {isArabic
                    ? 'لا يمكن عرض المعاينة في المتصفح. افتحها في نافذة جديدة أو قم بتنزيلها.'
                    : 'Preview cannot be displayed in this browser. Open it in a new tab or download.'}
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {isArabic ? 'فتح في نافذة جديدة' : 'Open in new tab'}
                </Button>
              </div>
            </object>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="w-4 h-4 mr-2" />
            {isArabic ? 'إغلاق' : 'Close'}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!pdfUrl || loading}
            className="bg-accent hover:bg-accent/90 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            {isArabic ? 'تحميل PDF' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BrochurePreview;